import Decimal from "decimal.js";

export function toFixed(
  floatNumber: any,
  numDigits: any,
  roundUp = false,
  withFallback = false,
  threshold = 0.98
) {
  const isNegative = floatNumber < 0;
  const absFloatNumber = Math.abs(floatNumber);
  const x = Math.pow(10, numDigits);
  const moduloResult = (absFloatNumber * x) % 1;
  const roundedValue =
    (roundUp || moduloResult > threshold
      ? Math.ceil(absFloatNumber * x)
      : Math.floor(absFloatNumber * x)) / x;
  return (
    (isNegative ? -roundedValue : roundedValue) ||
    (withFallback ? parseFloat(floatNumber as any) : 0)
  );
}

export function round(floatNumber: any, numDigits: any, withFallback = false) {
  const x = Math.pow(10, numDigits);
  const roundedValue = Math.round(floatNumber * x) / x;
  return roundedValue || (withFallback ? floatNumber : 0);
}

const getLocale = () => {
  return typeof navigator !== "undefined" && navigator.language
    ? navigator.language
    : undefined;
};

export const getIsDecimalCommaLocale = () => {
  const formatted = new Intl.NumberFormat(getLocale()).format(1.1);
  return formatted.includes(",");
};

export const getLocaleFloatString = (value?: string | number) => {
  const isNumber = typeof value === "number";
  let valueStr = isNumber ? value?.toString() : value;
  if (getIsDecimalCommaLocale()) {
    valueStr = valueStr?.replace(".", ",");
  } else {
    valueStr = valueStr?.replace(",", ".");
  }
  return valueStr;
};

export function countDecimals(num?: number) {
  var match = ("" + num).match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/);
  if (!match) {
    return 0;
  }
  return Math.max(
    0,
    // Number of digits right of decimal point.
    (match[1] ? match[1].length : 0) -
      // Adjust for scientific notation.
      (match[2] ? +match[2] : 0)
  );
}

const numberFormatterCache = new Map<string, Intl.NumberFormat>();

export function getLocaleStringWithPrecision(
  value: number,
  precision?: number,
  useRound = false,
  withFallback = false
) {
  const _precision =
    (isNaN(precision as any) ? countDecimals(value) : precision) ?? 0;
  const valueWithPrecision = useRound
    ? round(value, _precision, withFallback)
    : toFixed(value, _precision, false, withFallback);
  try {
    const userLocale = getLocale();
    const cacheKey = `${userLocale || "default"}_${_precision}`;
    if (!numberFormatterCache.has(cacheKey)) {
      const formatter = new Intl.NumberFormat(userLocale, {
        maximumFractionDigits: _precision,
      });
      numberFormatterCache.set(cacheKey, formatter);
    }
    return numberFormatterCache.get(cacheKey)!.format(valueWithPrecision);
  } catch (err) {
    return value.toString();
  }
}

export const addFloats = (float1: number, float2: number) => {
  return new Decimal(float1).add(float2).toNumber();
};

export const subtractFloats = (float1: number, float2: number) => {
  return new Decimal(float1).sub(float2).toNumber();
};

export const divideFloats = (float1: number, float2: number) => {
  return new Decimal(float1).div(float2).toNumber();
};

export const multiplyFloats = (float1: number, float2: number) => {
  return new Decimal(float1).mul(float2).toNumber();
};

export const avoidScientificNumber = (number: number | string) => {
  if (Math.abs(Number(number)) < 1.0) {
    const e = parseInt(number.toString().split("e-")[1]);
    if (e) {
      number = new Decimal(number)
        .mul(new Decimal(10).pow(new Decimal(e).sub(1)))
        .toString();
      number = "0." + new Array(e).join("0") + number.toString().substring(2);
    }
  } else {
    let e = parseInt(number.toString().split("+")[1]);
    if (e > 20) {
      e -= 20;
      number = new Decimal(number).div(new Decimal(10).pow(e)).toString();
      number = new Decimal(number).add(new Array(e + 1).join("0")).toString();
    }
  }
  return Number(number);
};

export const smartRound = (value: number, sigfigs: number = 4): number => {
  const absValue = Math.abs(value);
  if (absValue === 0) {
    return 0;
  }
  const magnitude = Math.floor(Math.log10(absValue));
  const decimals = Math.max(0, sigfigs - magnitude - 1);
  return toFixed(value, decimals);
};
